<div>
  <a routerLink="/service" class="back-link">
    <mat-icon class="material-symbols-outlined">arrow_back</mat-icon>
    Back to service menu
  </a>
</div>

<h1 class="heading">Fill missing required fields</h1>
<p class="sub-heading">
  Scan this database for required fields that have no value, then set the same value on every object
  that is missing the selected field. Existing values are never overwritten.
</p>
<p class="warning">
  Warning: apply writes one shared value onto all objects missing the field. Always dry-run first.
</p>

<div class="scan-row">
  <mat-form-field>
    <mat-label>Object type</mat-label>
    <mat-select [(ngModel)]="selectedObjectType">
      @for (objectType of objectTypes; track objectType.value) {
        <mat-option [value]="objectType.value">{{objectType.label}}</mat-option>
      }
    </mat-select>
  </mat-form-field>
  <button mat-flat-button color="primary" type="button" (click)="scan()" [disabled]="scanning">
    Scan
  </button>
  @if (scanning) {
    <mat-spinner diameter="24"></mat-spinner>
  }
</div>

@if (scanError) {
  <p class="error">{{scanError}}</p>
}

@if (missingFields.length) {
  <table mat-table [dataSource]="missingFields" class="fields-table">
    <ng-container matColumnDef="fieldTitle">
      <th mat-header-cell *matHeaderCellDef>Field</th>
      <td mat-cell *matCellDef="let row">{{row.fieldTitle}}</td>
    </ng-container>
    <ng-container matColumnDef="indexName">
      <th mat-header-cell *matHeaderCellDef>Index name</th>
      <td mat-cell *matCellDef="let row">{{row.indexName}}</td>
    </ng-container>
    <ng-container matColumnDef="count">
      <th mat-header-cell *matHeaderCellDef>Missing</th>
      <td mat-cell *matCellDef="let row">{{row.count}}</td>
    </ng-container>
    <ng-container matColumnDef="subObjectType">
      <th mat-header-cell *matHeaderCellDef>Sub-object type</th>
      <td mat-cell *matCellDef="let row">{{row.subObjectType || '—'}}</td>
    </ng-container>
    <ng-container matColumnDef="support">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let row">
        @if (row.unsupportedReason) {
          <span class="unsupported">Not supported</span>
        }
      </td>
    </ng-container>
    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: displayedColumns"
        [class.selected-row]="selectedField?.indexName === row.indexName"
        (click)="selectField(row)"></tr>
  </table>
} @else if (!scanning && !scanError) {
  <p class="hint">Select an object type and scan to list required fields that are empty.</p>
}

@if (selectedField) {
  <section class="field-panel">
    <h2>{{selectedField.fieldTitle}}</h2>
    <p class="hint">{{selectedField.indexName}} — {{previewCount}} object(s) missing this field</p>

    @if (selectedField.unsupportedReason) {
      <p class="warning">{{selectedField.unsupportedReason}}</p>
    } @else {
      @if (loadingField) {
        <mat-spinner diameter="24"></mat-spinner>
      }

      @if (previewObjects.length) {
        <h3>Preview</h3>
        <ul class="preview-list">
          @for (doc of previewObjects; track objectId(doc) || $index) {
            <li>
              {{objectName(doc) || objectId(doc)}}
              @if (objectIdentifier(doc)) {
                <span> ({{objectIdentifier(doc)}})</span>
              }
            </li>
          }
        </ul>
      }

      <h3>Value to set</h3>
      <p class="hint">The most used existing value is selected by default. You can pick another suggestion or enter a different value.</p>
      <mat-radio-group [(ngModel)]="selectedSuggestionValue" (ngModelChange)="onValueChange()">
        @for (suggestion of suggestions; track suggestion.value) {
          <mat-radio-button [value]="suggestion.value">
            {{suggestion.display_value}} ({{suggestion.count}})
          </mat-radio-button>
        }
        <mat-radio-button value="__custom__">Other value</mat-radio-button>
      </mat-radio-group>

      @if (selectedSuggestionValue === '__custom__') {
        <mat-form-field class="custom-value">
          <mat-label>Value (use a concept id for concept fields)</mat-label>
          <input matInput [(ngModel)]="customValue" (ngModelChange)="onValueChange()">
        </mat-form-field>
      }

      <div class="actions">
        <button mat-stroked-button type="button" (click)="dryRun()" [disabled]="!canDryRun">Dry run</button>
        <button mat-flat-button color="primary" type="button" (click)="apply()" [disabled]="!canApply">Apply</button>
        @if (working) {
          <mat-spinner diameter="24"></mat-spinner>
        }
      </div>

      @if (dryRunResult) {
        <p class="dry-run">
          Dry run: {{dryRunResult.missing_count}} object(s) will get
          "{{displayValue(dryRunResult.value)}}".
        </p>
      }
      @if (applyResult) {
        @if (applyResult.queued) {
          <p class="apply-result">
            Queued {{applyResult.missing_count}} object(s) as a background job.
            Progress is shown in Job status. Existing values will not be overwritten.
          </p>
        } @else {
          <p class="apply-result">
            Updated {{applyResult.updated}}, skipped (already set) {{applyResult.skipped_already_set}},
            failed {{applyResult.failed}}.
          </p>
        }
        @if (applyResult.errors?.length) {
          <pre class="error">{{applyResult.errors | json}}</pre>
        }
      }
    }

    @if (fieldError) {
      <p class="error">{{fieldError}}</p>
    }
  </section>
}
